/**
 * CalendarGrid — daily time-grid for the Schedule page.
 *
 * Renders an HH-column grid (default 7am–7pm) with grooming
 * time-blocks positioned by start_time + duration. Duration is the
 * actual minutes for the groomer's selected service (or 60 min
 * default), so a 90-min full groom looks 1.5× the height of a 60-min
 * bath-and-dry on the same day — what AC2 calls for.
 *
 * Pure presentational. Accepts already-resolved booking data
 * (customer/dog names, start time HH:MM, duration minutes) so the
 * page composes the right shape from the existing API surfaces.
 */
import { useMemo } from 'react';

export interface GroomingBlock {
  bookingId: string;
  customerName: string;
  dogNames: string[];
  /** HH:MM, e.g. "10:30" */
  startTime: string;
  /** Duration in minutes for this booking's grooming service. */
  durationMinutes: number;
  status: string;
  serviceName: string;
  /**
   * #984 — a service leg (an add-on hanging off a visit). Drawn like any
   * block, because it is real groomer time, but never counted as a booked
   * chair. Optional so existing callers and fixtures keep compiling.
   */
  isLeg?: boolean;
}

export interface CalendarGridProps {
  blocks: ReadonlyArray<GroomingBlock>;
  /** First hour to show (24h). Default 7. */
  dayStartHour?: number;
  /** Hour AFTER the last shown hour (24h). Default 19 (so 7am–7pm). */
  dayEndHour?: number;
  /** Pixel height per hour. Default 64 → 12 hours = 768px. */
  hourHeightPx?: number;
}

function hhmmToMinutes(hhmm: string): number {
  const parts = hhmm.split(':');
  const h = Number(parts[0]);
  const m = Number(parts[1] ?? '0');
  if (Number.isNaN(h) || Number.isNaN(m)) return 0;
  return h * 60 + m;
}

function hourLabel(hour24: number): string {
  if (hour24 === 0) return '12 AM';
  if (hour24 === 12) return '12 PM';
  if (hour24 < 12) return `${hour24} AM`;
  return `${hour24 - 12} PM`;
}

function statusClass(status: string): string {
  switch (status) {
    case 'checked_in':
      return 'ferro-calendar__block--checked-in';
    case 'checked_out':
      return 'ferro-calendar__block--checked-out';
    case 'cancelled':
    case 'no_show':
      return 'ferro-calendar__block--cancelled';
    default:
      return 'ferro-calendar__block--scheduled';
  }
}

export function CalendarGrid({
  blocks,
  dayStartHour = 7,
  dayEndHour = 19,
  hourHeightPx = 64,
}: CalendarGridProps): JSX.Element {
  const hours = useMemo(
    () =>
      Array.from(
        { length: Math.max(1, dayEndHour - dayStartHour) },
        (_, i) => dayStartHour + i,
      ),
    [dayStartHour, dayEndHour],
  );

  const positioned = useMemo(() => {
    const dayStartMin = dayStartHour * 60;
    const dayEndMin = dayEndHour * 60;
    const pxPerMin = hourHeightPx / 60;
    return blocks
      .map((b) => {
        const start = hhmmToMinutes(b.startTime);
        const end = start + Math.max(15, b.durationMinutes);
        const clampedStart = Math.max(dayStartMin, start);
        const clampedEnd = Math.min(dayEndMin, end);
        if (clampedEnd <= clampedStart) {
          return null;
        }
        return {
          ...b,
          top: (clampedStart - dayStartMin) * pxPerMin,
          height: (clampedEnd - clampedStart) * pxPerMin,
          inferiorStart: start < dayStartMin,
          inferiorEnd: end > dayEndMin,
        };
      })
      .filter((b): b is NonNullable<typeof b> => b !== null);
  }, [blocks, dayStartHour, dayEndHour, hourHeightPx]);

  const gridHeight = (dayEndHour - dayStartHour) * hourHeightPx;

  return (
    <div className="ferro-calendar" data-testid="calendar-grid">
      <div className="ferro-calendar__hours" style={{ height: gridHeight }}>
        {hours.map((h) => (
          <div
            key={h}
            className="ferro-calendar__hour-row"
            style={{ height: hourHeightPx }}
          >
            <span className="ferro-calendar__hour-label">{hourLabel(h)}</span>
            <span className="ferro-calendar__hour-line" />
          </div>
        ))}
      </div>
      <div
        className="ferro-calendar__tracks"
        style={{ height: gridHeight }}
        role="list"
        aria-label="Grooming appointments"
      >
        {positioned.length === 0 ? (
          <p className="ferro-calendar__empty">
            No grooming appointments scheduled.
          </p>
        ) : null}
        {positioned.map((b) => (
          <div
            key={b.bookingId}
            role="listitem"
            data-testid="calendar-block"
            className={`ferro-calendar__block ${statusClass(b.status)}`}
            style={{ top: b.top, height: b.height }}
            title={`${b.dogNames.join(', ')} · ${b.serviceName} · ${b.startTime}`}
          >
            <div className="ferro-calendar__block-head">
              <span className="ferro-calendar__block-time">{b.startTime}</span>
              <span className="ferro-calendar__block-duration">
                {b.durationMinutes} min
              </span>
            </div>
            <div className="ferro-calendar__block-body">
              <strong>{b.dogNames.join(', ') || 'Unnamed dog'}</strong>
              <span className="ferro-calendar__block-owner">
                {b.customerName} · {b.serviceName}
              </span>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
